import React, {
  useContext, useCallback, useEffect, useRef,
} from 'react';
import { Popover, Dropdown } from '@byte-design/ui';
import { observer, useLocalStore } from 'mobx-react';
import Styles from './index.module.scss';
import { Aiming, LeftC, RightC } from '@icon-park/react';
import { useTranslation } from 'react-i18next';
import { MonacoDiffEditor } from 'react-monaco-editor';
import { storeContext } from '@/pages/home/models/store';
import 'monaco-editor/esm/vs/editor/contrib/find/findController';

const options = {
  selectOnLineNumbers: true,
  scrollBeyondLastColumn: 10,
  automaticLayout: true,
  scrollBeyondLastLine: false,
  minimap: { enabled: false },
  readOnly: true,
};

export enum Type {
  router = 'router',
  api = 'api',
  gray = 'gray'
}

const Others = [
  { name: 'router_config', id: Type.router },
  { name: 'api_config', id: Type.api },
  { name: 'canary_config', id: Type.gray },
];

function CodeDiff() {
  const ref = useRef(null);
  const { t } = useTranslation();
  const State = useLocalStore(() => ({
    editor: null,
    diffCount: [],
    currentDiff: 0,
  }));
  const { diffStore } = useContext(storeContext);
  const { currentDiffInfo } = diffStore;

  const onChange = useCallback((id: string) => {
    diffStore.diffConfigType = id as Type;
    let searchString = '';
    if (id === 'router') {
      searchString = '路由配置';
    }
    if (id === 'api') {
      searchString = 'API配置';
    }
    if (id === 'gray') {
      searchString = '灰度配置';
    }
    const { range } = (State.editor as any).getModel().modified.findMatches(searchString)[0] || { };
    if (range) {
      (State.editor as any).revealRangeAtTop(range);
    }
  }, []);

  const editorDidMount = useCallback((editor) => {
    editor.onDidUpdateDiff(() => {
      // if (diffStore.interfaceList.length) {
      State.diffCount = editor.getLineChanges() || [];
      State.editor = editor;
      const { modifiedStartLineNumber } = State.diffCount[State.currentDiff] || { modifiedStartLineNumber: 0 };
      (State.editor as any).revealLineNearTop(modifiedStartLineNumber);
      // }
    });
  }, []);

  const goNext = useCallback(() => {
    if (State.currentDiff + 1 === State.diffCount.length) {
      return;
    }
    State.currentDiff++;

    const { modifiedStartLineNumber } = State.diffCount[State.currentDiff];
    (State.editor as any).revealLineNearTop(modifiedStartLineNumber);
  }, []);

  const goPrve = useCallback(() => {
    if (State.currentDiff === 0) {
      State.currentDiff = 0;
    } else {
      State.currentDiff--;
    }
    const { modifiedStartLineNumber } = State.diffCount[State.currentDiff];
    (State.editor as any).revealLineNearTop(modifiedStartLineNumber);
  }, []);

  useEffect(() => {
    if (State.editor) {
      // 编辑器滚动监听
      (State.editor as any).getModifiedEditor().onDidScrollChange(() => {
        const [range] = (State.editor as any).getModifiedEditor().getVisibleRanges();
        const { startLineNumber } = range;
        const currentDiff = State.diffCount.findIndex((item: any) => item.modifiedStartLineNumber >= startLineNumber);
        State.currentDiff = currentDiff === -1 ? State.diffCount.length - 1 : currentDiff;
      });
    }
  }, [State.editor]);

  return (
    <div className={Styles.container}>
      {/* 头部 */}
      <div className={Styles.top}>
        <div className={Styles.onlineVersion}>{t('online_version')}</div>
        <div className={Styles.nowVersion}>{t('now_version')}</div>
      </div>

      {/* 编辑器 */}
      <div className={Styles.main} ref={ref as any}>
        <MonacoDiffEditor
          // language="yaml"
          theme="vs"
          original={currentDiffInfo.oldCode}
          value={currentDiffInfo.newCode}
          options={options}
          editorDidMount={editorDidMount}
        />
      </div>

      <div className={Styles.footer}>
        {State.diffCount.length > 0 && (
        <LeftC
          size={14}
          className={`${Styles.left} ${State.currentDiff === 0 && Styles.disabled}`}
          onClick={goPrve}
        />
        )}
        <div className={Styles.diffMsg}>
          <div style={{ userSelect: 'none' }}>
            {t('code_diff_message', {
              count: State.diffCount.length,
              current: State.diffCount.length ? State.currentDiff + 1 : 0,
            })}
          </div>

          {State.diffCount.length > 0 && (
          <RightC
            size={14}
            className={`${Styles.right} ${State.currentDiff + 1 === State.diffCount.length && Styles.disabled}`}
            onClick={goNext}
          />
          )}

          {/* 显示其他的diff */}
          { currentDiffInfo.type === 'interface' && (
            <div className={Styles.select}>
              <Popover
                trigger="hover"
                placement="top"
                offset={{ x: 0, y: -6 }}
                getPopper={(
                  <Dropdown.Menu
                    onClick={onChange}
                    activeId={diffStore.diffConfigType}
                  >
                    {Others.map((list) => (
                      <Dropdown.MenuItem key={list.id} id={list.id}>
                        {t(list.name)}
                      </Dropdown.MenuItem>
                    ))}
                  </Dropdown.Menu>
                )}
              >
                <div className={Styles.selectInner}>
                  <Aiming size="16" style={{ marginRight: '4px' }} />
                  {t('focus_site')}
                </div>
              </Popover>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(observer(CodeDiff));
